import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { faFileShield, faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { log } from 'console';
import { File, Search, ShieldQuestion, Users } from 'lucide-react';
import { Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ChronologyViewer from './ChronologyViewer';
import DocumentsEditor from './DocumentsEditor';
import FactsEditor from './FactsEditor';
import InconsistenciesEditor from './InconsistenciesEditor';
import { InconsistencyReportView } from './inconsistency-report/InconsistencyReportView';
import PersonsEditor from './PersonsEditor';
import { OrgPermission } from '../../../../../../backend/src/organisation/organisation-permission';
import { TrackingEvents } from '../../../../analytics/events';
import { trackCustomEvent } from '../../../../analytics/Mixpanel';
import { useOrgPermissions } from '@/hooks/orgPermissions';

const DataView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const caseId = searchParams.get('caseId');

  const { isEnabled } = useOrgPermissions();

  const handleTabClick = (e: React.MouseEvent, path: string) => {
    navigate(path);
  };

  const NavItem = ({ title, route, icon }: { title: string; route: string; icon?: React.ReactNode }) => {
    const isActive = route ? location.pathname.split('?')[0].includes(route.split('?')[0]) : false;

    return (
      <div
        className={`mx-2 flex items-center gap-2 rounded px-2 py-2 text-xs transition-colors duration-150 hover:cursor-pointer hover:bg-gray-100 ${
          isActive ? 'border-gray-200 bg-gray-200 font-medium hover:bg-gray-200' : ''
        }`}
        onClick={(e) => {
          if (title === 'Entities') {
            trackCustomEvent(TrackingEvents.ClickEntitiesTab);
          }

          handleTabClick(e, route);
        }}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{title}</span>
      </div>
    );
  };

  const NavGroup = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-3">
      <div className="mb-1 border-b px-2 pt-2 pb-1 text-xs font-semibold text-gray-600">{title}</div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );

  return (
    <div className="flex h-full w-full border-b">
      {/* Sidebar container */}
      <div
        className={`transition-width relative mr-2 flex h-full w-36 flex-col border-r border-gray-200/80 bg-gray-50 py-1 duration-500 ease-in-out`}
      >
        <div className="flex h-full flex-col overflow-y-auto px-1 py-2">
          <NavGroup title="Facts">
            <NavItem
              title="Facts"
              route={`/app/chronos/case-editor/data/facts?caseId=${caseId}`}
              icon={<Search className="h-3 w-3 shrink-0 text-gray-700" />}
            />
            <NavItem
              title="Chronologies"
              route={`/app/chronos/case-editor/data/chronology?caseId=${caseId}`}
              icon={<FontAwesomeIcon icon={faTimeline} className="h-3 w-3 shrink-0 text-gray-700" />}
            />
          </NavGroup>

          {isEnabled(OrgPermission.Inconsistencies) && (
            <NavGroup title="Inconsistencies">
              <NavItem
                title="Inconsistencies"
                route={`/app/chronos/case-editor/data/inconsistencies?caseId=${caseId}`}
                icon={<ShieldQuestion className="h-3 w-3 shrink-0 text-gray-700" />}
              />
              <NavItem
                title="Reports"
                route={`/app/chronos/case-editor/data/inconsistency-report?caseId=${caseId}`}
                icon={<FontAwesomeIcon icon={faFileShield} className="h-3 w-3 shrink-0 text-gray-700" />}
              />
            </NavGroup>
          )}

          <NavGroup title="Entities">
            <NavItem
              title="Entities"
              route={`/app/chronos/case-editor/data/persons?caseId=${caseId}`}
              icon={<Users className="h-3 w-3 shrink-0 text-gray-700" />}
            />
          </NavGroup>

          <NavGroup title="Documents">
            <NavItem
              title="Documents"
              route={`/app/chronos/case-editor/data/documents?caseId=${caseId}`}
              icon={<File className="h-3 w-3 shrink-0 text-gray-700" />}
            />
          </NavGroup>
        </div>
      </div>

      {/* Main content area */}
      <div className="h-full w-full overflow-x-auto overflow-y-hidden px-2">
        <Routes>
          <Route path="facts" element={<FactsEditor />} />
          <Route path="documents" element={<DocumentsEditor />} />
          <Route path="chronology/*" element={<ChronologyViewer />} />
          <Route path="persons" element={<PersonsEditor />} />
          {isEnabled(OrgPermission.Inconsistencies) && (
            <>
              <Route path="inconsistencies" element={<InconsistenciesEditor />} />
              <Route path="inconsistency-report/*" element={<InconsistencyReportView />} />
            </>
          )}
        </Routes>
      </div>
    </div>
  );
};

export default DataView;
